/**
 * QuizQuestion Component
 * Display quiz question with member photo and answer options
 */

import { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { quizService, type QuizQuestion as QuizQuestionType } from '../../services/quiz.service';

export interface QuizQuestionProps {
  question: QuizQuestionType;
  currentQuestionNumber: number;
  totalQuestions: number;
  onAnswerSelected: (optionId: number) => void;
  onNextQuestion: () => void;
  selectedAnswer: number | null;
  isCorrect: boolean | null;
  /** Option the server says was right — known only after the answer is submitted */
  correctOption: number | null;
  showNextButton: boolean;
}

export const QuizQuestion = ({
  question,
  currentQuestionNumber,
  totalQuestions,
  onAnswerSelected,
  onNextQuestion,
  selectedAnswer,
  isCorrect,
  correctOption,
  showNextButton: _showNextButton,
}: QuizQuestionProps) => {
  // _showNextButton is available via props but handled by parent component
  const [tempSelectedAnswer, setTempSelectedAnswer] = useState<number | null>(null);
  const [showingResults, setShowingResults] = useState(false);

  // Load the photo by question id (TW-374: a member-id URL would give the answer away)
  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    let blobUrl: string | null = null;
    setImageBlobUrl(null);
    setImageLoading(true);
    quizService.fetchQuizPhoto(question.id).then((url) => {
      blobUrl = url;
      if (mounted) {
        setImageBlobUrl(url);
        setImageLoading(false);
      } else if (url) {
        URL.revokeObjectURL(url);
      }
    });
    return () => {
      mounted = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [question.id]);

  // Reset temp selection when question changes
  useEffect(() => {
    setTempSelectedAnswer(null);
    setShowingResults(false);
  }, [question.id]);

  const progress = (currentQuestionNumber / totalQuestions) * 100;
  const isLastQuestion = currentQuestionNumber === totalQuestions;

  const getOptionClassName = (optionId: number) => {
    const baseClass =
      'quiz-option p-4 rounded-lg transition-colors duration-200 text-left focus:outline-none focus:ring-0 active:ring-2 active:ring-blue-500 cursor-pointer';

    // After submission - showing results (only if isCorrect is determined)
    if (selectedAnswer !== null && showingResults && isCorrect !== null) {
      // This option was selected
      if (selectedAnswer === optionId) {
        if (isCorrect) {
          return `${baseClass} border-2 border-green-500 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-200`;
        } else {
          return `${baseClass} border-2 border-red-500 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-200`;
        }
      }

      // Show correct answer if user selected wrong
      if (isCorrect === false && optionId === correctOption) {
        return `${baseClass} border-2 border-green-500 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-200`;
      }

      // Other options when results showing
      return `${baseClass} border-2 border-strong bg-surface-app opacity-50`;
    }

    // Temporary selection OR waiting for API result (keep blue)
    if (tempSelectedAnswer === optionId ||
        (showingResults && selectedAnswer === optionId && isCorrect === null)) {
      return `${baseClass} border-2 border-blue-500 bg-surface`;
    }

    // No answer selected yet - hoverable
    return `${baseClass} border-2 border-strong hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20`;
  };

  const getOptionIcon = (optionId: number) => {
    // Only show icons after result is determined (not while waiting for API)
    if (!showingResults || selectedAnswer === null || isCorrect === null) return null;

    // This option was selected
    if (selectedAnswer === optionId) {
      if (isCorrect) {
        return (
          <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        );
      } else {
        return (
          <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        );
      }
    }

    // Show check mark for correct answer if user selected wrong
    if (isCorrect === false && optionId === correctOption) {
      return (
        <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
      );
    }

    return null;
  };

  return (
    <div>
      {/* Progress Bar */}
      <div className="bg-surface rounded-lg shadow py-4 px-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-body">
              Question {currentQuestionNumber} of {totalQuestions}
            </span>
            <div className="flex-1 bg-surface-secondary rounded-full h-2 w-48">
              <div
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="bg-surface rounded-lg shadow p-8">
        <div className="text-center mb-8">
          <h2 className="text-xl font-medium text-heading mb-6">Who is this person?</h2>

          {/* Member Photo */}
          <div className="flex justify-center mb-6">
            <div className="relative w-48 h-48">
              {imageLoading || !imageBlobUrl ? (
                <div className="absolute inset-0 bg-surface-secondary rounded-lg flex items-center justify-center">
                  <svg
                    className="animate-spin h-8 w-8 text-purple-600"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
              ) : (
                <img
                  src={imageBlobUrl}
                  alt="Team Member"
                  className="w-48 h-48 rounded-lg object-cover shadow-lg"
                />
              )}
            </div>
          </div>
        </div>

        {/* Answer Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {question.options.map((option, index) => (
            <button
              key={option.option_id}
              onClick={() => !showingResults && setTempSelectedAnswer(option.option_id)}
              disabled={showingResults}
              className={getOptionClassName(option.option_id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-meta">
                    {index + 1}.
                  </span>
                  <span className="font-medium text-heading">{option.member_name}</span>
                </div>
                {getOptionIcon(option.option_id)}
              </div>
            </button>
          ))}
        </div>

        {/* Submit/Next Button */}
        {(tempSelectedAnswer !== null || showingResults) && (
          <div className="text-center mt-8">
            <Button
              onClick={() => {
                if (!showingResults && tempSelectedAnswer !== null) {
                  // Submit answer and show results
                  setShowingResults(true);
                  onAnswerSelected(tempSelectedAnswer);
                } else if (showingResults) {
                  // Already showing results, advance immediately
                  onNextQuestion();
                }
              }}
            >
              {!showingResults ? 'Submit Answer' : (isLastQuestion ? 'View Results' : 'Next Question')}
              <svg className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
